import React, { useRef, useState } from 'react';
import { FormField } from '@/shared/components/ui/FormField';
import { Button } from '@/shared/components/ui/Button';
import { api } from '@/lib/api';

interface VideoUploaderFieldProps {
  label: string;
  description?: string;
  value?: string;
  onChange: (value: string | undefined) => void;
}

/**
 * Campo de vídeo do customizer. O arquivo passa pela API (`POST /uploads/video`,
 * até 100MB), que grava público no S3 como as imagens, e o campo guarda a URL
 * pública. Envio direto do navegador ao bucket é barrado pelo CORS dele.
 */
/** Mesmo teto da rota `POST /uploads/video` (multer `uploadVideo`). */
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;

async function uploadVideo(file: File, onProgress: (percent: number) => void): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('uploads/video?folder=websites', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (event: { loaded: number; total?: number }) => {
      if (event.total) onProgress(Math.round((event.loaded / event.total) * 100));
    },
  });
  return response.data.data.url as string;
}

export const VideoUploaderField: React.FC<VideoUploaderFieldProps> = ({
  label,
  description,
  value,
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(null);
    if (!file.type.startsWith('video/')) {
      setError('Arquivo inválido: escolha um vídeo (MP4, MOV, WebM...).');
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      setError('O vídeo passa de 100 MB. Envie um arquivo menor.');
      return;
    }
    setProgress(0);
    try {
      const url = await uploadVideo(file, setProgress);
      onChange(url);
    } catch {
      setError('Não foi possível enviar o vídeo. Tente novamente.');
    } finally {
      setProgress(null);
    }
  };

  const uploading = progress !== null;

  return (
    <FormField label={label} description={description}>
      <div className="space-y-3">
        {value && (
          <video
            src={value}
            controls
            preload="metadata"
            className="w-full rounded-lg"
            style={{ maxHeight: 200, backgroundColor: '#000' }}
          />
        )}

        {uploading && (
          <div
            className="w-full h-2 rounded-full overflow-hidden"
            style={{ backgroundColor: '#e2e8f0' }}
            data-testid="video-uploader-progress"
            role="progressbar"
            aria-valuenow={progress ?? 0}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="h-full"
              style={{ width: `${progress}%`, backgroundColor: '#8b5cf6', transition: 'width .2s' }}
            />
          </div>
        )}

        {error && (
          <p role="alert" style={{ color: '#ef4444', fontSize: 12 }} data-testid="video-uploader-error">
            {error}
          </p>
        )}

        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={handleFile}
            data-testid="video-uploader-input"
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? `Enviando ${progress}%` : value ? 'Trocar vídeo' : 'Enviar vídeo'}
          </Button>
          {value && !uploading && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onChange(undefined)}
              data-testid="video-uploader-remove"
            >
              Remover
            </Button>
          )}
        </div>
      </div>
    </FormField>
  );
};
